import React, { useState, useEffect } from 'react';
import { Upload, Eye, Download, X, CheckCircle, FileText, AlertCircle, AlertTriangle, Star, Search, Filter, Loader, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PDFMerger from 'pdf-merger-js/browser';
import jsPDF from 'jspdf'; // Add this import

const DocumentsUpload = () => {
  const { user, logout } = useAuth();
  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;

  // ... [Keep all your existing state and documentCategories as-is] ...

  const documentCategories = [
    {
      title: "Personal Identity Documents",
      priority: 1,
      docs: ["AADHAR_CARD", "PAN_CARD", "VOTER_ID", "PASSPORT", "DRIVING_LICENSE"]
    },
    {
      title: "Banking & Financial",
      priority: 1,
      docs: ["BANK_DETAILS_PROOF"]
    },
    {
      title: "Statutory Forms",
      priority: 2,
      docs: ["PF_FORM", "NOMINATION_AND_DECLARATION_PF_FORM", "GRATUITY_FORM"]
    },
    {
      title: "Educational Credentials",
      priority: 2,
      docs: ["EDUCATIONAL_DOCUMENTS"]
    },
    {
      title: "Previous Employment",
      priority: 2,
      docs: ["EXPERIENCE_LETTER_RELIEVING_LETTER", "LAST_EMPLOYMENT_CTC_LETTER", "LAST_3_MONTH_SALARY_SLIP", "RESIGNATION_ACCEPTANCE", "RELIEVING_LETTER"]
    },
    {
      title: "Salary & Increments",
      priority: 3,
      docs: ["INCREMENT_LETTER"]
    },
    {
      title: "Other Documents",
      priority: 3,
      docs: ["OTHERS"]
    }
  ];

  const [uploadedDocs, setUploadedDocs] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [notification, setNotification] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState({});
  const [dragActive, setDragActive] = useState(null);

  useEffect(() => {
    fetchUploadedDocuments();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchUploadedDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-urls?eid=${user.eid}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const urlsData = await response.json();
      
      const uploadedDocsData = {};
      Object.entries(urlsData).forEach(([docType, url]) => {
        if (url) {
          uploadedDocsData[docType] = {
            name: `${user.eid}_${docType}`,
            type: 'application/pdf',
            serverFilename: `${user.eid}_${docType}`,
            url: url,
            uploadedAt: new Date().toISOString()
          };
        }
      });
      
      setUploadedDocs(uploadedDocsData);
    } catch (error) {
      console.error('Error fetching documents:', error);
      showNotification('Failed to load existing documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const wordToBeCapitalized = ["pan", "pf", "id", "ctc"];
  const formatDocName = (key) => {
    return key.split('_').map(word => 
      wordToBeCapitalized.includes(word.toLowerCase()) ? word.toUpperCase() : word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const handleDrag = (e, docType) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(docType);
    } else if (e.type === "dragleave") {
      setDragActive(null);
    }
  };

  const handleDrop = (e, docType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);
    
    const files = Array.from(e.dataTransfer.files);
    addFilesToPending(docType, files);
  };

  const handleFileSelect = (docType, event) => {
    const files = Array.from(event.target.files);
    addFilesToPending(docType, files);
    event.target.value = '';
  };

  const addFilesToPending = (docType, files) => {
    if (!files || files.length === 0) return;

    const newFiles = files.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type
    }));

    setPendingFiles(prev => ({
      ...prev,
      [docType]: [...(prev[docType] || []), ...newFiles]
    }));
  };

  const removePendingFile = (docType, fileId) => {
    setPendingFiles(prev => {
      const updated = { ...prev };
      const files = updated[docType] || [];
      const fileToRemove = files.find(f => f.id === fileId);
      
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      
      updated[docType] = files.filter(f => f.id !== fileId);
      if (updated[docType].length === 0) {
        delete updated[docType];
      }
      return updated;
    });
  };

  // IMPROVED: Better image to PDF conversion using jsPDF
  const convertImageToPdf = async (imageFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const img = new Image();
          
          img.onload = () => {
            try {
              // Create jsPDF instance
              const pdf = new jsPDF({
                orientation: img.width > img.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [img.width, img.height]
              });

              // Add image to PDF
              pdf.addImage(
                e.target.result, 
                imageFile.type === 'image/png' ? 'PNG' : 'JPEG',
                0, 
                0, 
                img.width, 
                img.height
              );

              // Get PDF as blob
              const pdfBlob = pdf.output('blob');
              resolve(pdfBlob);
            } catch (error) {
              reject(error);
            }
          };
          
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = e.target.result;
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(imageFile);
    });
  };

  const uploadPendingFiles = async (docType) => {
    const files = pendingFiles[docType];
    if (!files || files.length === 0) return;

    setUploadingDoc(docType);

    try {
      let fileToUpload;
      let fileName;

      // If multiple files, merge PDFs first
      if (files.length > 1) {
        showNotification('Processing and merging files...', 'success');
        
        try {
          const merger = new PDFMerger();
          
          // Add all files to merger
          for (let i = 0; i < files.length; i++) {
            const fileObj = files[i];
            
            // Convert images to PDF or add PDF directly
            if (fileObj.type === 'application/pdf') {
              await merger.add(fileObj.file);
            } else if (fileObj.type.startsWith('image/')) {
              // Convert image to PDF using jsPDF
              showNotification(`Converting ${fileObj.name} to PDF...`, 'success');
              const pdfBlob = await convertImageToPdf(fileObj.file);
              await merger.add(pdfBlob);
            } else {
              console.warn(`Skipping ${fileObj.name} - only PDF and images can be merged`);
              showNotification(`Skipped ${fileObj.name} - unsupported format`, 'error');
              continue;
            }
          }
          
          // Get merged PDF as blob
          const mergedPdfBlob = await merger.saveAsBlob();
          fileToUpload = mergedPdfBlob;
          fileName = `${user.eid}_${docType}`;
          
          showNotification('Files merged successfully!', 'success');
        } catch (mergeError) {
          console.error('Merge error:', mergeError);
          showNotification('Failed to merge files. Uploading first file only.', 'error');
          // Fallback to first file if merge fails
          const firstFile = files[0].file;
          
          // If first file is an image, convert it to PDF
          if (firstFile.type.startsWith('image/')) {
            fileToUpload = await convertImageToPdf(firstFile);
          } else {
            fileToUpload = firstFile;
          }
          fileName = `${user.eid}_${docType}`;
        }
      } else {
        // Single file
        const singleFile = files[0].file;
        
        // If it's an image, convert to PDF
        if (singleFile.type.startsWith('image/')) {
          showNotification('Converting image to PDF...', 'success');
          fileToUpload = await convertImageToPdf(singleFile);
        } else {
          fileToUpload = singleFile;
        }
        
        fileName = `${user.eid}_${docType}`;
      }

      // Upload the file (merged or single)
      const formData = new FormData();
      const renamedFile = new File([fileToUpload], fileName, { 
        type: 'application/pdf'  // Always PDF now
      });
      formData.append('file', renamedFile);

      const response = await fetch(`${API_BASE_URL}?eid=${user.eid}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Clear pending files and revoke URLs
      files.forEach(fileObj => {
        URL.revokeObjectURL(fileObj.preview);
      });

      setPendingFiles(prev => {
        const updated = { ...prev };
        delete updated[docType];
        return updated;
      });

      await fetchUploadedDocuments();

      showNotification(
        files.length > 1 
          ? `${files.length} files merged and uploaded successfully for ${formatDocName(docType)}!`
          : `File uploaded successfully for ${formatDocName(docType)}!`, 
        'success'
      );
    } catch (error) {
      console.error('Upload error:', error);
      showNotification(`Failed to upload ${formatDocName(docType)}: ${error.message}`, 'error');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleRemove = async (docType) => {
    try {
      const resp = await fetch(`${API_BASE_URL}?eid=${user.eid}&file=${user.eid+"_"+docType}.pdf`, {
        method: 'DELETE'
      });
      if (!resp.ok) {
        showNotification('Failed to delete document', 'error');
        throw new Error('Delete failed');
      }
      setUploadedDocs(prev => {
        const newDocs = { ...prev };
        delete newDocs[docType];
        return newDocs;
      });
      showNotification(`${formatDocName(docType)} deleted successfully!`, 'success');
    } catch (error) {
      console.log(error.message);
      showNotification(`Failed to delete ${formatDocName(docType)}`, 'error');
    }
  };

  const handlePreview = async (docType) => {
    setPreviewLoading(true);
    try {
      const doc = uploadedDocs[docType];
      
      if (!doc.url) {
        throw new Error('Document URL not available');
      }

      const response = await fetch(doc.url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      setPreviewDoc({ 
        type: docType, 
        ...doc,
        url: blobUrl,
        blobUrl: blobUrl
      });
    } catch (error) {
      console.error('Preview error:', error);
      showNotification('Failed to load preview', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/merge?eid=${user.eid}`, {
        method: 'GET'
      });
      if (!resp.ok) {
        showNotification('Failed to download documents', 'error');
        throw new Error('Download failed');
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${user.eid}_merged.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      showNotification('All documents downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download documents', 'error');
    }
  };

  const handleDownloadSingle = async (docType) => {
    try {
      const doc = uploadedDocs[docType];
      
      if (!doc.url) {
        throw new Error('Document URL not available');
      }

      const response = await fetch(doc.url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name || doc.serverFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download error:', error);
      showNotification(`Failed to download ${formatDocName(docType)}`, 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const totalDocs = documentCategories.reduce((acc, cat) => acc + cat.docs.length, 0);
  const uploadedCount = Object.keys(uploadedDocs).length;
  const progress = (uploadedCount / totalDocs) * 100;

  const filteredCategories = documentCategories
    .map(category => ({
      ...category,
      docs: category.docs.filter(doc => {
        const matchesSearch = formatDocName(doc).toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPriority = filterPriority === 'all' || category.priority === parseInt(filterPriority);
        const matchesPending = !showOnlyPending || !uploadedDocs[doc];
        return matchesSearch && matchesPriority && matchesPending;
      })
    }))
    .filter(category => category.docs.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg animate-slideIn ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white flex items-center gap-3`}>
          {notification.type === 'success' ? (
            <CheckCircle size={24} />
          ) : (
            <AlertCircle size={24} />
          )}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3">
            <Loader className="animate-spin text-indigo-600" size={48} />
            <p className="text-gray-700 font-medium">Loading documents...</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Document Upload Portal</h1>
          <p className="text-gray-600 mb-4">Please upload all required documents for verification</p>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span className="font-medium">Upload Progress</span>
              <span className="font-semibold">{uploadedCount} of {totalDocs} documents</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {progress === 100 ? '🎉 All documents uploaded!' : `${(100 - progress).toFixed(0)}% remaining`}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {uploadedCount > 0 && (
              <button
                onClick={handleDownloadAll}
                className="flex items-center gap-2 bg-green-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-green-700 transition-all hover:shadow-lg text-sm md:text-base"
              >
                <Download size={20} />
                Download All ({uploadedCount})
              </button>
            )}
            
            <button
              onClick={() => setShowOnlyPending(!showOnlyPending)}
              className={`flex items-center gap-2 px-4 md:px-6 py-2 rounded-lg transition-all text-sm md:text-base ${
                showOnlyPending 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Filter size={20} />
              {showOnlyPending ? 'Show All' : 'Show Pending Only'}
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        </div>

        {/* Document Categories */}
        <div className="space-y-6">
          {filteredCategories.map((category, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
              <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="text-indigo-600" size={24} />
                  {category.title} {category.title==="Banking & Financial"&&<span className='text-sm text-gray-600 ml-2'><b className='text-red-600'>*</b> Cancelled cheque, passbook copy, or bank statement file to be uploaded.</span>}
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.docs.map(docType => {
                  const isUploaded = uploadedDocs[docType];
                  const hasPendingFiles = pendingFiles[docType] && pendingFiles[docType].length > 0;
                  
                  return (
                    <div 
                      key={docType} 
                      className={`border-2 rounded-lg p-4 transition-all ${
                        isUploaded 
                          ? 'border-green-300 bg-green-50' 
                          : hasPendingFiles
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-dashed border-gray-300 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-700 flex-1 leading-tight">
                          {[{"EXPERIENCE_LETTER_RELIEVING_LETTER": "Experience Letter / Relieving Letter", "INCREMENT_LETTER": "Appointment / Increment Letter"}].find((item) => item[docType])?.[docType] || formatDocName(docType)}
                        </h3>
                        {isUploaded && (
                          <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                        )}
                      </div>

                      {!isUploaded ? (
                        <>
                          {/* Drag and Drop Area */}
                          <div
                            onDragEnter={(e) => handleDrag(e, docType)}
                            onDragLeave={(e) => handleDrag(e, docType)}
                            onDragOver={(e) => handleDrag(e, docType)}
                            onDrop={(e) => handleDrop(e, docType)}
                            className={`border-2 border-dashed rounded-lg p-4 mb-3 transition-all ${
                              dragActive === docType
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-300 bg-gray-50'
                            }`}
                          >
                            <label className="cursor-pointer block text-center">
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => handleFileSelect(docType, e)}
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                multiple
                              />
                              <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                              <p className="text-xs text-gray-600 mb-1">
                                <span className="text-indigo-600 font-medium">Click to browse</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">PDF, JPG, PNG, DOC (Max 10MB)</p>
                            </label>
                          </div>

                          {/* Pending Files Preview */}
                          {hasPendingFiles && (
                            <div className="space-y-2 mb-3">
                              <p className="text-xs font-medium text-gray-700">Files to upload:</p>
                              <div className="max-h-40 overflow-y-auto space-y-2">
                                {pendingFiles[docType].map((fileObj) => (
                                  <div key={fileObj.id} className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
                                    {fileObj.type.startsWith('image/') ? (
                                      <img src={fileObj.preview} alt="" className="w-10 h-10 object-cover rounded" />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                        <FileText size={20} className="text-gray-500" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-gray-700 truncate">{fileObj.name}</p>
                                      <p className="text-xs text-gray-500">{formatFileSize(fileObj.size)}</p>
                                    </div>
                                    <button
                                      onClick={() => removePendingFile(docType, fileObj.id)}
                                      className="text-red-500 hover:text-red-700 p-1"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Upload Button */}
                              <button
                                onClick={() => uploadPendingFiles(docType)}
                                disabled={uploadingDoc === docType}
                                className={`w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium ${
                                  uploadingDoc === docType ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {uploadingDoc === docType ? (
                                  <>
                                    <Loader size={16} className="animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <Upload size={16} />
                                    {pendingFiles[docType].length > 1 
                                      ? `Merge & Upload ${pendingFiles[docType].length} Files` 
                                      : `Upload File`
                                    }
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-xs text-gray-600 truncate font-medium" title={isUploaded.name}>
                            📄 {isUploaded.name}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePreview(docType)}
                              disabled={previewLoading}
                              className="flex-1 flex items-center justify-center gap-1 bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition-colors text-xs disabled:opacity-50"
                            >
                              {previewLoading ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                <Eye size={14} />
                              )}
                              Preview
                            </button>
                            <button
                              onClick={() => handleDownloadSingle(docType)}
                              className="flex items-center justify-center bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition-colors"
                              title="Download"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              onClick={() => handleRemove(docType)}
                              className="flex items-center justify-center bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 transition-colors"
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredCategories.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No documents found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Preview Modal */}
        {previewDoc && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {formatDocName(previewDoc.type)}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">{previewDoc.name}</p>
                </div>
                <button
                  onClick={() => {
                    if (previewDoc.blobUrl) {
                      URL.revokeObjectURL(previewDoc.blobUrl);
                    }
                    setPreviewDoc(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 p-2 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100">
                {previewDoc.url && (previewDoc.type.includes('image') || previewDoc.name.match(/\.(jpg|jpeg|png|gif)$/i)) ? (
                  <img src={previewDoc.url} alt="Preview" className="max-w-full h-auto mx-auto rounded-lg shadow-lg" />
                ) : previewDoc.url && (previewDoc.type.includes('pdf') || previewDoc.name.match(/\.pdf$/i)) ? (
                  <iframe src={previewDoc.url} className="w-full h-[70vh] rounded-lg shadow-lg" title="PDF Preview"></iframe>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-600 mb-2">Preview not available for this file type</p>
                    <p className="text-sm text-gray-500 mb-4">{previewDoc.name}</p>
                    <button
                      onClick={() => handleDownloadSingle(previewDoc.type)}
                      className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      <Download size={20} />
                      Download File
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DocumentsUpload;